// Validation, and the words it says.
//
// Two rules govern every message below.
//
// The first is who is reading it. Somebody is standing at a stand with a queue
// behind them, holding a coffee, being talked at. They get one short sentence
// naming what to do. No field names repeated back at them, no "invalid", no
// "please", no apology, and never a rule quoted at somebody who did not write
// it. docs/brand/voice.md governs the wording as strictly here as it does a
// page of the website.
//
// The second is what a failure costs, and the answer stopped being the same
// for the two shape checks on 2026-08-27.
//
// A required phone whose national length is wrong blocks. An optional phone
// still only warns. Section 3.5 of the field library refused a loose regex
// that locked out real numbers; a country's own length is not that regex.
// US and CA are 10. GB is 10 after the trunk zero. A hard "10 everywhere"
// would refuse a French mobile.
//
// An email shape failure blocks. That is not the argument above being
// overruled, it is the argument reaching the one rule it does not cover, and
// the distinction is set out at EMAIL_SHAPE rather than here because that is
// where the next person will be standing when they need it. Everything a
// client configured as required blocks, and nothing else does.

import {
  CEILINGS,
  fieldOptions,
  isOtherValue,
  type FieldDefinition,
  type FieldValue,
  type FieldValues,
} from "./types.js";
import { looksLikePhone, nationalLengthOk, phoneCountryOf, phoneLooksTooLong, PHONE_TOO_LONG } from "./phone.js";
import { suggestEmail } from "./suggest.js";

/**
 * What to say about one answer.
 *
 * `suggestion` hangs off `ok` rather than off a fourth state, and that is
 * deliberate twice over. A suggested address is an answer that passed: nothing
 * is wrong with it, it may well be exactly right, and a state of its own would
 * be a thing every caller's switch had to learn about in order to conclude that
 * it does not stop anybody finishing. It also cannot coexist with a refusal,
 * because a refused address has a message telling the person what to do and a
 * second line of advice underneath it is two instructions for one field.
 */
export type Verdict =
  | { state: "ok"; suggestion?: string }
  | { state: "warn"; message: string }
  | { state: "block"; message: string };

const OK: Verdict = { state: "ok" };
const block = (message: string): Verdict => ({ state: "block", message });
const warn = (message: string): Verdict => ({ state: "warn", message });

/** The id the renderer gives the "other" choice when a client allows one. */
export const OTHER_OPTION_ID = "other";

/**
 * Characters that will not survive the trip.
 *
 * Tab, newline and carriage return are excluded from the list for the
 * multi-line types and included for the single-line ones, which is the whole
 * difference between the two. These arrive by paste, from a document or a
 * spreadsheet, rather than by anybody typing them.
 */
const CONTROL_ANY = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const CONTROL_SINGLE_LINE = /[\u0000-\u001F\u007F]/;

/*
 * Something before an @, something after it, a dot, and two characters of
 * domain. Nothing else.
 *
 * It blocks, as of 2026-08-27, and the looseness above is the entire reason it
 * is allowed to. A string that fails this rule has no @ in it, or nothing on
 * one side of it, or no dot in the domain. That is not an address some unusual
 * mail server happens to accept and this regular expression is too crude to
 * see; it is not an address. Refusing on it therefore costs close to nothing in
 * real people turned away, and a typed address that is not an address is a lead
 * the client paid for and can never contact, found out about weeks later if at
 * all. Between a refusal somebody can fix while they are still standing there
 * and a bounce nobody sees, the refusal wins.
 *
 * The case against strictness has not been dropped. It has moved.
 *
 * Anything tighter than this rule, a list of valid top-level domains, a length
 * limit on the local part, RFC 5322 in full, starts refusing addresses that
 * deliver. A warning that fires on a working address teaches people to tap past
 * warnings, including the ones that matter, and a block that fires on one loses
 * the lead outright, which is worse than the failure it was trying to prevent.
 * So the rule for anybody extending this: a stricter email check goes in the
 * warn layer, and never in the block layer. Adding a case beside this one is
 * the change to argue about first. Adding one that warns is not.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/** 19 August 2026. Never 08/19/2026, per voice.md section 4. */
export function formatDate(iso: string): string {
  const match = ISO_DATE.exec(iso);
  if (!match) return iso;
  const [year, month, day] = iso.split("-");
  const name = MONTHS[Number(month) - 1];
  if (!name || !day || !year) return iso;
  return `${Number(day)} ${name} ${year}`;
}

/** Code points, not UTF-16 units, because that is what a person counts and what Postgres counts. */
export const countCharacters = (value: string): number => [...value].length;

export function isEmpty(value: FieldValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (isOtherValue(value)) return value.option_id === "";
  return false;
}

/** The ceiling a client's `max_length` is clamped to, whatever they configured. */
export function effectiveMaxLength(field: FieldDefinition): number {
  switch (field.type) {
    case "text":
      return Math.min(field.max_length ?? CEILINGS.text, CEILINGS.text);
    case "long_text":
      return Math.min(field.max_length ?? CEILINGS.long_text, CEILINGS.long_text);
    case "email":
      return CEILINGS.email;
    case "phone":
      return CEILINGS.phone;
    case "survey_question":
      return Math.min(field.max_length ?? CEILINGS.long_text, CEILINGS.long_text);
    default:
      return CEILINGS.text;
  }
}

const tooLong = (value: string, max: number): Verdict | null => {
  const over = countCharacters(value.trim()) - max;
  return over > 0 ? block(`That is ${over} characters too long.`) : null;
};

const missing = (field: FieldDefinition): Verdict => {
  if (field.type === "survey_question") {
    switch (field.answer_format) {
      case "single_choice":
        return block("Choose one.");
      case "multi_choice":
        return block("Choose at least one.");
      case "rating_scale":
        return block("Choose a rating.");
      case "free_text":
        return block("Fill this in.");
    }
  }

  switch (field.type) {
    case "single_select":
    case "status":
      return block("Choose one.");
    case "multi_select":
      return block("Choose at least one.");
    case "boolean":
      return block(`Choose ${field.true_label} or ${field.false_label}.`);
    default:
      return block("Fill this in.");
  }
};

const CONFUSED = block("Something is wrong with this answer. Ask a crew member.");

function checkText(value: FieldValue, field: FieldDefinition, multiline: boolean): Verdict {
  if (typeof value !== "string") return CONFUSED;
  const control = multiline ? CONTROL_ANY : CONTROL_SINGLE_LINE;
  if (control.test(value)) return block("Some characters in that will not save. Type it again.");
  return tooLong(value, effectiveMaxLength(field)) ?? OK;
}

function checkChoice(value: FieldValue, field: FieldDefinition, allowOther: boolean): Verdict {
  const ids = new Set(fieldOptions(field).map((o) => o.id));
  if (allowOther) ids.add(OTHER_OPTION_ID);

  if (isOtherValue(value)) {
    if (!ids.has(value.option_id)) return block("That choice has gone. Pick another.");
    if (value.other_text.trim() === "") return block("Type your answer, or choose one of the others.");
    return tooLong(value.other_text, CEILINGS.other_text) ?? OK;
  }

  if (typeof value !== "string") return CONFUSED;
  return ids.has(value) ? OK : block("That choice has gone. Pick another.");
}

function checkMulti(value: FieldValue, field: FieldDefinition, min?: number, max?: number): Verdict {
  if (!Array.isArray(value)) return CONFUSED;
  const ids = new Set(fieldOptions(field).map((o) => o.id));
  if (value.some((id) => !ids.has(id))) return block("One of those choices has gone. Pick again.");
  if (min !== undefined && value.length < min) return block(`Choose at least ${min}.`);
  if (max !== undefined && value.length > max) return block(`Choose no more than ${max}.`);
  return OK;
}

/**
 * One field, one verdict.
 *
 * A field that is not visible is never validated here: the caller filters
 * first, through validateFieldSet. A hidden required field that blocks the
 * button is a form nobody can finish and nobody can see why.
 */
export function validateField(field: FieldDefinition, value: FieldValue): Verdict {
  if (isEmpty(value)) {
    return field.required ? missing(field) : OK;
  }

  switch (field.type) {
    case "text":
      return checkText(value, field, false);

    case "long_text":
      return checkText(value, field, true);

    case "number": {
      // The input hands back what was typed rather than a number, so that a
      // half-typed "1." is still on screen while it is being typed.
      const asNumber = typeof value === "number" ? value : Number(String(value).trim());
      if (typeof value !== "number" && typeof value !== "string") return CONFUSED;
      if (!Number.isFinite(asNumber)) return block("Use digits only.");
      if (field.integer_only && !Number.isInteger(asNumber)) return block("Use a whole number.");
      if (field.min !== undefined && asNumber < field.min) return block(`Use ${field.min} or more.`);
      if (field.max !== undefined && asNumber > field.max) return block(`Use ${field.max} or less.`);
      return OK;
    }

    case "date": {
      if (typeof value !== "string") return CONFUSED;
      if (!ISO_DATE.test(value)) return block("Use a real date.");
      // Round-tripped rather than parsed, because Date.parse quietly turns
      // 2026-02-31 into 3 March.
      const parsed = new Date(`${value}T00:00:00Z`);
      if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
        return block("Use a real date.");
      }
      if (field.earliest !== undefined && value < field.earliest) {
        return block(`Choose a date on or after ${formatDate(field.earliest)}.`);
      }
      if (field.latest !== undefined && value > field.latest) {
        return block(`Choose a date on or before ${formatDate(field.latest)}.`);
      }
      return OK;
    }

    case "email": {
      if (typeof value !== "string") return CONFUSED;
      const long = tooLong(value, effectiveMaxLength(field));
      if (long) return long;

      const address = value.trim();
      if (!EMAIL_SHAPE.test(address)) return block("Use an address like name@company.com.");

      // Well formed, and still quite possibly wrong. Nothing above this line
      // can see gmial.com and neither can anything after the tablet, so the
      // one person who can is asked. suggest.ts holds the argument.
      const suggestion = suggestEmail(address);
      return suggestion === null ? OK : { state: "ok", suggestion };
    }

    case "phone": {
      if (typeof value !== "string") return CONFUSED;
      const long = tooLong(value, effectiveMaxLength(field));
      if (long) return long;
      const country = phoneCountryOf(value, field.default_country ?? "GB");
      if (field.required && !nationalLengthOk(value, country)) {
        return block("That number is the wrong length.");
      }
      if (phoneLooksTooLong(value, country)) {
        return warn(PHONE_TOO_LONG);
      }
      return looksLikePhone(value)
        ? OK
        : warn("That number looks wrong. Carry on if it is right.");
    }

    case "single_select":
      return checkChoice(value, field, field.allow_other === true);

    case "multi_select":
      return checkMulti(value, field, field.min_selections, field.max_selections);

    case "boolean":
      return typeof value === "boolean" ? OK : CONFUSED;

    case "status":
      return checkChoice(value, field, false);

    case "survey_question":
      switch (field.answer_format) {
        case "single_choice":
          return checkChoice(value, field, false);
        case "multi_choice":
          return checkMulti(value, field);
        case "rating_scale": {
          const min = field.scale_min ?? 1;
          const max = field.scale_max ?? CEILINGS.rating_points;
          const asNumber = typeof value === "number" ? value : Number(value);
          if (!Number.isInteger(asNumber) || asNumber < min || asNumber > max) {
            return block(`Choose a number between ${min} and ${max}.`);
          }
          return OK;
        }
        case "free_text":
          return checkText(value, field, true);
      }
  }
}

export type Audience = "attendee" | "crew";

export type FieldSetVerdict = {
  /** Every visible field's verdict, keyed by `field_id`. */
  verdicts: Record<string, Verdict>;
  /** Field ids that stop the registration completing, in field order. */
  blocking: string[];
  /** Field ids carrying a warning, which never stop anything. */
  warnings: string[];
  /**
   * Field ids whose answer passed and has a likelier reading offered beside it.
   *
   * Listed alongside the other two so that a caller counting what is on screen
   * does not have to walk the verdicts to find these. It stops nothing, and a
   * caller that reads it as a reason to hold the button back has misread it.
   */
  suggestions: string[];
};

/**
 * Whether a field is on screen, against one earlier field. Section 4.
 *
 * One level, no nesting. A `visible_when` pointing at a field that is itself
 * conditional is refused at configuration by checkFieldSet, so this function
 * does not have to walk anything.
 */
export function isVisible(field: FieldDefinition, values: FieldValues, audience: Audience): boolean {
  if (field.type === "status" && audience !== "crew") return false;
  const rule = field.visible_when;
  if (!rule) return true;
  return values[rule.field_id] === rule.equals;
}

/** Every visible field, validated, with the blocking ones named in field order. */
export function validateFieldSet(
  fields: readonly FieldDefinition[],
  values: FieldValues,
  audience: Audience = "attendee",
): FieldSetVerdict {
  const verdicts: Record<string, Verdict> = {};
  const blocking: string[] = [];
  const warnings: string[] = [];
  const suggestions: string[] = [];

  for (const field of fields) {
    if (!isVisible(field, values, audience)) continue;
    const verdict = validateField(field, values[field.field_id] ?? null);
    verdicts[field.field_id] = verdict;
    if (verdict.state === "block") blocking.push(field.field_id);
    if (verdict.state === "warn") warnings.push(field.field_id);
    if (verdict.state === "ok" && verdict.suggestion !== undefined) suggestions.push(field.field_id);
  }

  return { verdicts, blocking, warnings, suggestions };
}
